import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import useGetPortfolioFamilies from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioFamilies";
import useGetPortfolioFamiliesById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioFamiliesById";
import { portfolioPlanByIdKeyMaker } from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import { getPortfolioPlanById } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanById";
import {
  BasePermission,
  PortfolioFamilyFamilyChild,
  PortfolioFamilyPlanChild,
} from "@fartherfinance/frontend/api/Types";

import {
  basePermissionOptions,
  taxTypeOptions,
} from "../PortfolioBuilder/constants";
import { TaxTypeEnum } from "../PortfolioBuilder/reducer/Types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { DropdownMenuOption } from "@src/sharedComponents/CustomDropdownBase/MenuOptionsWithCheckIfSelected/MenuOptionsWithCheckIfSelected";
import BaseDropdown from "@src/sharedComponents/Dropdown/BaseDropdown";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import TaxStatusLabel from "@src/sharedComponents/TaxStatusLabel/TaxStatusLabel";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./CreateCustomOrPartnerModelModal.module.css";

export const MIN_PORTFOLIO_NAME_LENGTH = 3;

const isPlanNotFamily = (
  plan: PortfolioFamilyFamilyChild | PortfolioFamilyPlanChild
): plan is PortfolioFamilyPlanChild => plan.type === "Portfolio-Plan";

const NameStringRegex = /^[a-zA-Z0-9\s\-'\/]+$/;
const DoubleCharsRegex = /(\s\s)|(\'\')|(\/\/)/; // checks for double spaces, double single quotes, and double back slashes

const isValidName = (input: string) => {
  if (DoubleCharsRegex.test(input)) {
    return false;
  }

  if (NameStringRegex.test(input)) {
    return true;
  }

  return false;
};

const NameInput = "NameInput";
const FirmInput = "FirmInput";
const SeriesInput = "SeriesInput";
const AllocationInput = "AllocationInput";

interface NewCustomModelDetails {
  modelName: string;
  firm: undefined;
  series: undefined;
  allocation: undefined;
  taxType: TaxTypeEnum;
  basePermission: BasePermission;
}

interface NewPartnerModelDetails {
  modelName: string;
  firm: string;
  series: string;
  allocation: string;
  taxType: TaxTypeEnum;
  basePermission: BasePermission;
}

export type NewModelDetails = NewCustomModelDetails | NewPartnerModelDetails;

interface Form {
  NameInput: string;
  FirmInput: string;
  SeriesInput: string;
  AllocationInput: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (model: NewModelDetails) => void;
  currentModelName?: string;
  currentFirm?: string;
  currentSeries?: string;
  currentAllocation?: string;
  currentTaxType?: TaxTypeEnum;
  currentBasePermission?: BasePermission;
}

// This component is used only by admins to create any type of portfolio: Private, Public-Organization, Public-Platform
const CreateCustomOrPartnerModelModal: React.FC<PropsWithChildren<Props>> = ({
  onClose,
  onSubmit,
  isOpen,
  currentModelName,
  currentFirm,
  currentSeries,
  currentAllocation,
  currentTaxType,
  currentBasePermission,
}) => {
  const [taxTypeOption, setTaxTypeOption] = useState<
    DropdownMenuOption<TaxTypeEnum>
  >(taxTypeOptions.find((el) => el.id === currentTaxType) ?? taxTypeOptions[0]);

  const [basePermissionOption, setBasePermissionOption] = useState<
    DropdownMenuOption<BasePermission>
  >(
    basePermissionOptions.find((el) => el.id === currentBasePermission) ??
      basePermissionOptions[0]
  );

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      NameInput: currentModelName ?? "",
      FirmInput: currentFirm ?? "",
      SeriesInput: currentSeries ?? "",
      AllocationInput: currentAllocation ?? "",
    },
  });

  const {
    getValues,
    formState: { isValid },
    setValue,
    watch,
  } = methods;

  const notify = useStatusNotification();

  const auth = useAdvisorRequestAuth();

  const portfolioFamilies = useGetPortfolioFamilies({ pageSize: "100" }, auth);

  const [modelName, firm, series, allocation] = watch([
    NameInput,
    FirmInput,
    SeriesInput,
    AllocationInput,
  ]);

  const partnerFieldsArePartiallyFilled = useMemo(
    () =>
      (firm !== "" && series === "" && allocation === "") ||
      (firm === "" && series !== "" && allocation === "") ||
      (firm === "" && series === "" && allocation !== "") ||
      (firm !== "" && series !== "" && allocation === "") ||
      (firm === "" && series !== "" && allocation !== "") ||
      (firm !== "" && series === "" && allocation !== ""),
    [firm, series, allocation]
  );

  const portfolioFamilyId = useMemo(() => {
    const matches = (portfolioFamilies.data?.data ?? []).filter(
      (pf) => pf.name === firm
    );

    if (matches.length > 0) {
      return matches[0]?.portfolioFamilyId;
    }

    return undefined;
  }, [firm, portfolioFamilies.data]);

  const portfolioFamily = useGetPortfolioFamiliesById(
    portfolioFamilyId ?? null,
    auth
  );

  const portfolioIdFromSeries = useMemo(() => {
    const matches = (portfolioFamily.data?.data.children ?? []).filter(
      (pfc) => pfc.type === "Family" && pfc.portfolioFamily.name === series
    );

    if (matches.length > 0 && matches[0]?.type === "Family") {
      return matches[0]?.portfolioFamily.portfolioFamilyId;
    }

    return undefined;
  }, [series, portfolioFamily.data]);

  const portfolioFamily2 = useGetPortfolioFamiliesById(
    portfolioIdFromSeries ?? null,
    auth
  );

  const portfolioIdFromAllocation = useMemo(() => {
    const matches = (portfolioFamily2.data?.data.children ?? []).filter(
      (pfc) => pfc.type === "Family" && pfc.portfolioFamily.name === allocation
    );

    if (matches.length > 0 && matches[0]?.type === "Family") {
      return matches[0]?.portfolioFamily.portfolioFamilyId;
    }

    return undefined;
  }, [allocation, portfolioFamily2.data]);

  const portfolioPlan = useGetPortfolioFamiliesById(
    portfolioIdFromAllocation ?? null,
    auth
  );

  const matchingPortfolioPlansWithoutDetails = useMemo(() => {
    const matches = (portfolioPlan.data?.data.children ?? []).filter(
      (pp) =>
        pp.type === "Portfolio-Plan" && pp.portfolioPlan.name === modelName
    );

    return matches;
  }, [modelName, portfolioPlan.data]);

  const matchingPortfolioPlans = useQueries({
    queries:
      auth === null
        ? []
        : matchingPortfolioPlansWithoutDetails
            .filter((p) => isPlanNotFamily(p))
            .map((pp) => {
              return {
                queryKey: portfolioPlanByIdKeyMaker(
                  pp.portfolioPlan.portfolioPlanId,
                  {}
                ),
                queryFn: () =>
                  getPortfolioPlanById(
                    pp.portfolioPlan.portfolioPlanId,
                    {},
                    auth
                  ),
              };
            }),
  });

  const taxTypesFromMatchedPlans = useMemo(() => {
    if (matchingPortfolioPlans.some((pp) => pp.isLoading)) {
      return [];
    }

    const plansTags = matchingPortfolioPlans.map(
      (pp) => pp.data?.data.plan.tags ?? []
    );

    return plansTags
      .map((tags) => {
        if (tags.length === 0) {
          return "any";
        } else if (tags.includes("Taxable")) {
          return "taxable";
        } else if (tags.includes("TaxExempt")) {
          return "tax_exempt";
        }

        return undefined;
      })
      .filter((tt) => tt !== undefined);
  }, [matchingPortfolioPlans]);

  useEffect(() => {
    if (taxTypesFromMatchedPlans?.some((tt) => tt === taxTypeOption.id)) {
      notify(
        "Portfolio for that firm, with that name, series, allocation and tax type already exists",
        "Warning"
      );
    }
  }, [taxTypesFromMatchedPlans, taxTypeOption.id, notify]);

  const onContinue = () => {
    const { NameInput, FirmInput, SeriesInput, AllocationInput } = getValues();

    if (NameInput.length < MIN_PORTFOLIO_NAME_LENGTH) {
      notify("Portfolio name must have at least 3 characters", "Error");
      return;
    }

    if (FirmInput === "" || SeriesInput === "" || AllocationInput === "") {
      onSubmit({
        modelName: NameInput,
        firm: undefined,
        series: undefined,
        allocation: undefined,
        taxType: taxTypeOption.id,
        basePermission: basePermissionOption.id,
      });
      return;
    }

    onSubmit({
      modelName: NameInput,
      firm: FirmInput,
      series: SeriesInput,
      allocation: AllocationInput,
      taxType: taxTypeOption.id,
      basePermission: basePermissionOption.id,
    });
  };

  const onSetTaxTypeOption = (tto: DropdownMenuOption<TaxTypeEnum>) =>
    setTaxTypeOption(tto);

  const onSetBasePermissionOption = (
    bpo: DropdownMenuOption<BasePermission>
  ) => {
    if (bpo.id === "Private") {
      setValue(FirmInput, "");
      setValue(SeriesInput, "");
      setValue(AllocationInput, "");
    }

    setBasePermissionOption(bpo);
  };

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text className={styles.title} variant="title2">
        Set up the model
      </Text>

      <FormProvider {...methods}>
        <FormTextField name={NameInput} label={"Name"} placeholder={""} />

        <Text className={styles.label} variant="bodySmall" color={"textSubtle"}>
          Must be a unique name
        </Text>

        <FormTextField
          name={FirmInput}
          label={"Asset Management Firm"}
          disabled={basePermissionOption.id === "Private"}
          placeholder={""}
          rules={{
            validate: {
              isValidName: (input) => {
                if (input === "") {
                  // empty string is allowed
                  return undefined;
                }

                if (input.length < 2) {
                  return "Must be at least 2 characters";
                }

                if (
                  input.length >= 1 &&
                  basePermissionOption.id === "Private"
                ) {
                  return "Private models cannot have a firm";
                }

                if (!isValidName(input)) {
                  return "Invalid characters used";
                }

                return undefined;
              },
            },
          }}
        />

        <Spacer verticalSpacing={"24px"} />

        <FormTextField
          name={SeriesInput}
          label={"Model Series"}
          placeholder={""}
          disabled={basePermissionOption.id === "Private"}
          rules={{
            validate: {
              isValidName: (input) => {
                if (input === "") {
                  // empty string is allowed
                  return undefined;
                }

                if (input.length < 2) {
                  return "Must be at least 2 characters";
                }

                if (
                  input.length >= 1 &&
                  basePermissionOption.id === "Private"
                ) {
                  return "Private models cannot have a series";
                }

                if (!isValidName(input)) {
                  return "Invalid characters used";
                }

                return undefined;
              },
            },
          }}
        />

        <Spacer verticalSpacing={"24px"} />

        <FormTextField
          name={AllocationInput}
          label={"Asset Allocation"}
          placeholder={""}
          disabled={basePermissionOption.id === "Private"}
          rules={{
            validate: {
              isValidName: (input) => {
                if (input === "") {
                  // empty string is allowed
                  return undefined;
                }

                if (input.length < 2) {
                  return "Must be at least 2 characters";
                }

                if (
                  input.length >= 1 &&
                  basePermissionOption.id === "Private"
                ) {
                  return "Private models cannot have an allocation";
                }

                if (!isValidName(input)) {
                  return "Invalid characters used";
                }

                return undefined;
              },
            },
          }}
        />

        <Spacer verticalSpacing={"16px"} />
      </FormProvider>

      {currentTaxType !== "DirectIndexedSleeve" && (
        <BaseDropdown
          title={<TaxStatusLabel />}
          curItemLabel={taxTypeOption.label}
          curItemId={taxTypeOption.id}
          options={taxTypeOptions.filter(
            // don't allow DirectIndexedSleeve for now
            (el) => el.id !== "DirectIndexedSleeve"
          )}
          onSelect={onSetTaxTypeOption}
        />
      )}

      <BaseDropdown
        title={"Base Permission"}
        curItemLabel={basePermissionOption.label}
        curItemId={basePermissionOption.id}
        options={basePermissionOptions}
        onSelect={onSetBasePermissionOption}
      />

      <Stack
        spacing={2}
        direction={"row"}
        marginTop={"24px"}
        justifyContent={"flex-end"}
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="text"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Continue"
          buttonType="primary"
          variant="contained"
          disabled={!isValid || partnerFieldsArePartiallyFilled}
          onClick={onContinue}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default CreateCustomOrPartnerModelModal;
